import { useMemo, useState } from "react";
import { SectionHeader } from "./ui";
import { cleanMediaCaption, isLikelyBrokenUploadUrl } from "./utils";

export const GallerySection = ({ media, loading }) => {
  const [failed, setFailed] = useState({});
  const [selectedIndex, setSelectedIndex] = useState(null);
  const galleryItems = useMemo(() => (Array.isArray(media) ? media : [])
    .filter((m) => m?.url && !isLikelyBrokenUploadUrl(m.url) && (m.placement || "gallery") === "gallery")
    .map((m) => ({
      id: m.id || m.url,
      url: m.url,
      caption: cleanMediaCaption(m.caption),
      alt: cleanMediaCaption(m.alt_text) || cleanMediaCaption(m.title),
    }))
    .filter((m) => !failed[m.url])
    .slice(0, 8), [media, failed]);

  // Sem fotos a secção não aparece (nem no menu).
  if (!loading && !galleryItems.length) return null;

  return (
    <section id="galeria" className="public-section public-gallery-section py-10 sm:py-12">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHeader
          k="Galeria"
          title="Momentos da nossa comunidade"
          desc="Fotos recentes dos cultos e da vida da igreja."
        />
        {loading ? (
          <div className="public-gallery-grid grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={`g-sk-${i}`} className="aspect-[4/3] rounded-2xl bg-slate-200 dark:bg-slate-700/70 animate-pulse" />
            ))}
          </div>
        ) : galleryItems.length ? (
          <div className="public-gallery-grid grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {galleryItems.map((item, index) => (
              <button
                type="button"
                key={item.id || item.url}
                onClick={() => setSelectedIndex(index)}
                aria-label={`Abrir imagem ${index + 1}${item.caption ? `: ${item.caption}` : ""}`}
                className="group relative aspect-[4/3] overflow-hidden rounded-2xl border border-white dark:border-slate-900/60 bg-white dark:bg-slate-900/50 shadow-sm"
              >
                <img
                  src={item.url}
                  alt={item.alt || item.caption || "Foto da igreja"}
                  className="h-full w-full object-cover transition duration-500 group-hover:scale-105"
                  loading="lazy"
                  decoding="async"
                  onError={() => setFailed((prev) => ({ ...prev, [item.url]: true }))}
                />
                {item.caption ? (
                  <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-slate-900/70 to-transparent px-3 py-2 text-xs text-white opacity-0 group-hover:opacity-100 transition">
                    {item.caption}
                  </div>
                ) : null}
              </button>
            ))}
          </div>
        ) : null}
      </div>
      {selectedIndex !== null && galleryItems[selectedIndex] ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/80 p-4"
          role="dialog"
          aria-modal="true"
          aria-label="Imagem ampliada da galeria"
          onClick={() => setSelectedIndex(null)}
        >
          <div className="relative flex max-h-[90vh] w-full max-w-5xl items-center justify-center" onClick={(event) => event.stopPropagation()}>
            <img
              src={galleryItems[selectedIndex].url}
              alt={galleryItems[selectedIndex].alt || galleryItems[selectedIndex].caption || "Foto da igreja"}
              className="max-h-[78vh] max-w-full rounded-2xl object-contain shadow-2xl"
            />
            <button
              type="button"
              aria-label="Fechar galeria"
              onClick={() => setSelectedIndex(null)}
              className="absolute right-0 top-0 rounded-full bg-white dark:bg-slate-900/90 px-3 py-1 text-2xl font-semibold text-slate-900 dark:text-slate-100 shadow-lg"
            >
              ×
            </button>
            {galleryItems.length > 1 ? (
              <>
                <button
                  type="button"
                  aria-label="Imagem anterior"
                  onClick={() => setSelectedIndex((selectedIndex - 1 + galleryItems.length) % galleryItems.length)}
                  className="absolute left-2 rounded-full bg-white dark:bg-slate-900/90 px-4 py-2 text-2xl font-semibold text-slate-900 dark:text-slate-100 shadow-lg sm:left-4"
                >
                  ‹
                </button>
                <button
                  type="button"
                  aria-label="Imagem seguinte"
                  onClick={() => setSelectedIndex((selectedIndex + 1) % galleryItems.length)}
                  className="absolute right-2 rounded-full bg-white dark:bg-slate-900/90 px-4 py-2 text-2xl font-semibold text-slate-900 dark:text-slate-100 shadow-lg sm:right-4"
                >
                  ›
                </button>
              </>
            ) : null}
            {galleryItems[selectedIndex].caption ? (
              <div className="absolute bottom-0 left-1/2 w-full -translate-x-1/2 rounded-b-2xl bg-slate-950/70 px-4 py-3 text-center text-sm text-white">
                {galleryItems[selectedIndex].caption}
              </div>
            ) : null}
          </div>
        </div>
      ) : null}
    </section>
  );
};
